import clsx from 'clsx'
import {
  type ChangeEvent,
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  useEffect,
  useRef,
} from 'react'

interface Props extends HTMLProps<HTMLTextAreaElement> {
  error?: string
  name: string
  autoExpand?: boolean
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void
}

export const TextAreaField = forwardRef(
  (
    { error, name, onChange, value, autoExpand, className, ...rest }: Props,
    ref?: ForwardedRef<HTMLTextAreaElement | null>,
  ) => {
    const localRef = useRef<HTMLTextAreaElement | null>(null)
    useAutosizeTextArea(localRef.current, value, autoExpand)

    useEffect(() => {
      if (!error) return localRef.current?.setCustomValidity('')
      localRef.current?.setCustomValidity(error)
      localRef.current?.reportValidity()
    }, [error])

    function localOnChange(e: ChangeEvent<HTMLTextAreaElement>) {
      // Do not allow leading space
      e.target.value = e.target.value.trimStart()
      e.target.setCustomValidity('')
      onChange?.(e)
    }

    function setRef(element: HTMLTextAreaElement | null) {
      localRef.current = element
      if (!ref) return
      if (typeof ref === 'function') ref(element)
      else ref.current = element
    }

    return (
      <textarea
        autoComplete="off"
        data-lpignore
        id={name}
        name={name}
        onChange={localOnChange}
        ref={setRef}
        value={value}
        className={clsx(className, { 'resize-none': autoExpand })}
        {...rest}
      />
    )
  },
)

const useAutosizeTextArea = (
  textAreaRef: HTMLTextAreaElement | null,
  value: string | number | readonly string[],
  enabled: boolean,
) => {
  useEffect(() => {
    if (textAreaRef && enabled) {
      // We need to reset the height momentarily to get the correct scrollHeight for the textarea
      textAreaRef.style.height = '0px'
      textAreaRef.style.overflow = 'hidden'
      const scrollHeight = textAreaRef.scrollHeight
      textAreaRef.style.height = `${scrollHeight}px`
    }
  }, [textAreaRef, value, enabled])
}
